import { useMemo } from 'react';
import { ChipList } from '../../../../ui-components/variant/chip/ChipList';
import { getVariationName, getVariationValue } from '../../../../utils/variant-helpers';
import type { ProductVariant, ProductVariantAttribute } from '../../../../bff-api/products/model';

export interface ChipListProps {
    chipListItems: ProductVariant[];
    variationAttributes?: ProductVariantAttribute[];
    activeId?: string;
    label?: string;
    onChange: (activeProductId: string) => void;
    size?: 'small' | 'default';
}

// Purpose: Connects the chip list to the buy box variants component and prepare the data for the chip list
export const ChipListConnector = ({
    activeId,
    onChange,
    chipListItems,
    variationAttributes,
    label,
    size = 'default',
}: ChipListProps) => {
    const activeItem = useMemo<ProductVariant | undefined>(
        () => chipListItems.find((variantParam) => variantParam.productId === activeId),
        [chipListItems, activeId],
    );

    const variationName = useMemo(
        () => getVariationName(activeItem?.variationValues ?? {}, variationAttributes ?? []),
        [activeItem, variationAttributes],
    );
    const chips = useMemo(
        () =>
            chipListItems.map((variant) => ({
                id: variant.productId,
                label: getVariationName(variant.variationValues, variationAttributes ?? []),
                value: getVariationValue(variant.variationValues, variationAttributes ?? []),
            })),
        [chipListItems, variationAttributes],
    );

    const handleClick = (id?: string): void => {
        if (id) {
            onChange(id);
        }
    };

    return (
        <ChipList
            onClick={handleClick}
            variationName={variationName}
            chipListItems={chips}
            label={label}
            activeId={activeItem?.productId}
            size={size}
        />
    );
};

ChipListConnector.defaultProps = {
    variationAttributes: [],
    activeId: undefined,
    label: undefined,
};
